/**
 * Option helpers for the SFTP Push Response Status Mapping table.
 * The field column uses the same condition-list field catalog as the QueryBuilder
 * first dropdown (`config.fields`). The value column uses valueOptions / top_values
 * for the selected field (or `response_status` when no field is selected).
 */
import {
  normalizeQueryBuilderOptions,
  QueryBuilderConfig,
  QueryBuilderOption,
} from "../../../../../data-model/queryBuilder";
import { getFieldDef } from "../../../../../components/queryBuilder/queryBuilderUtils";
import type { QueryBuilderValueSearchOption } from "../../../../../components/queryBuilder/useQueryBuilderValueSearch";
import {
  CmmInputUniqueValueItem,
  CmmInputUniqueValuesDoc,
  normalizeTopValues,
} from "../../validationFunnel/pages/conditionList/cmmInputUniqueValuesTypes";

export const RESPONSE_STATUS_FIELD = "response_status";

/**
 * Strips count suffixes from condition-list labels so tags match stored values.
 *
 * @param label - Raw label, often with a `(count)` suffix
 * @return Label without the trailing count segment
 */
function stripLabelCount(label: string): string {
  return label.replace(/\s*\(\d+\)$/, "");
}

/**
 * Coerces a condition-list top value entry to a select option.
 *
 * @param entry - Raw top_values item from the CMM document
 * @return Normalized value/label pair
 */
function topValueToOption(
  entry: CmmInputUniqueValueItem | string | number | boolean,
): QueryBuilderOption {
  const isItemShape =
    entry != null && typeof entry === "object" && "value" in entry;
  const value = isItemShape ? (entry as CmmInputUniqueValueItem).value : entry;
  const count = isItemShape ? (entry as CmmInputUniqueValueItem).count : undefined;
  const valueStr = String(value ?? "").slice(0, 80);
  const suffix = String(value ?? "").length > 80 ? "…" : "";
  const countPart = count !== undefined ? ` (${count})` : "";
  return {
    value: String(value ?? ""),
    label: `${valueStr}${suffix}${countPart}`,
  };
}

/**
 * Appends selected entries missing from the base option list.
 *
 * @param baseOptions - Primary option catalog
 * @param selectedValues - Stored values that must remain selectable
 * @param labelForValue - Optional resolver for missing-entry labels
 * @return De-duplicated options including preserved selections
 */
function withSelectedOptions(
  baseOptions: QueryBuilderOption[],
  selectedValues: string[],
  labelForValue?: (value: string) => string,
): QueryBuilderOption[] {
  const seen = new Set(baseOptions.map((option) => option.value));
  const merged = [...baseOptions];

  for (const value of selectedValues) {
    const trimmed = value.trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    merged.push({
      value: trimmed,
      label: labelForValue?.(trimmed) ?? trimmed,
    });
  }

  return merged;
}

/**
 * Builds field dropdown options — same catalog as the QueryBuilder field selector.
 *
 * @param conditionConfig - Query builder config used by the SFTP Push condition builders
 * @param selectedFields - Stored field names that must remain selectable
 * @return Field options with human labels (e.g. "Form Name", "SFTP Status")
 */
export function getConditionFieldSelectOptions(
  conditionConfig: QueryBuilderConfig,
  selectedFields: string[],
): QueryBuilderOption[] {
  const baseOptions = normalizeQueryBuilderOptions(
    conditionConfig.fields.map((field) => ({
      value: field.value,
      label: field.label,
    })),
  );

  return withSelectedOptions(baseOptions, selectedFields, (value) => {
    const match = getFieldDef(conditionConfig, value);
    return match?.label ?? value;
  });
}

/**
 * Reads condition-list top_values for one column from the CMM document.
 *
 * @param cmmDoc - CMM input unique values document
 * @param fieldName - Column name to match
 * @return Normalized options from top_values
 */
function getCmmTopValueOptions(
  cmmDoc: CmmInputUniqueValuesDoc | null,
  fieldName: string,
): QueryBuilderOption[] {
  const column = (cmmDoc?.columns ?? []).find(
    (col) =>
      col.column_name === fieldName ||
      col.field_name === fieldName ||
      col.display_name === fieldName,
  );
  if (!column) return [];

  return normalizeTopValues(column.top_values)
    .map(topValueToOption)
    .filter((option) => option.value.trim() !== "");
}

/**
 * Builds value dropdown options for the currently selected condition field.
 *
 * @param conditionConfig - Query builder config used by the SFTP Push condition builders
 * @param cmmDoc - CMM input unique values document
 * @param fieldName - Selected field/column name; defaults to `response_status`
 * @param selectedValues - Stored values that must remain selectable
 * @return Options for MultiSelectWithTag / useQueryBuilderValueSearch
 */
export function getFieldValueSelectOptions(
  conditionConfig: QueryBuilderConfig,
  cmmDoc: CmmInputUniqueValuesDoc | null,
  fieldName: string,
  selectedValues: string[],
): QueryBuilderValueSearchOption[] {
  const resolvedField = fieldName.trim() || RESPONSE_STATUS_FIELD;
  const fieldDef = getFieldDef(conditionConfig, resolvedField);
  const configOptions = normalizeQueryBuilderOptions(fieldDef?.valueOptions ?? []);
  const cmmOptions = getCmmTopValueOptions(cmmDoc, resolvedField);

  const merged = withSelectedOptions(
    [...configOptions, ...cmmOptions],
    selectedValues,
  );

  return merged.map((option) => ({
    id: option.value,
    label: stripLabelCount(option.label),
  }));
}
